import {
    ChangeDetectionStrategy,
    Component,
    computed,
    inject,
    linkedSignal,
    signal,
    ViewEncapsulation,
} from "@angular/core";
import { LayoutWidget } from "../layouts/se.layout.widget.abstract";
import { Layouts } from "../layouts/se.layouts";
import { SEEventDetailsStore } from "./se.event.details.store";
import { SeOrgSecurityStore } from "../se.org.security.store";
import { LoginStore } from "../../../stores/login.store";
import { DataIngestionService, IngestionPoint } from "../../data-ingestion/data.ingestion.service";
import { IngestionPointCategory } from "../../data-ingestion/data.ingestion.const";
import { IngestionAccess } from "../../data-ingestion/ingestion.access";
import { s25Resource } from "../../../util/s25.resource";
import { S25Util } from "../../../util/s25-util";
import { IngestionFormTileComponent } from "../../data-ingestion/ingestion.form.tile.component";
import { IngestionFormComponent } from "../../data-ingestion/ingestion.form.component";
import { createEventEditHydrate } from "../../data-ingestion/event.form.hydrate.factory";

@Component({
    standalone: true,
    selector: "s25-ng-se-event-edit-forms-widget",
    template: `
        @if (canShowForms()) {
            <section class="event-edit-forms">
                @if (activeFormId()) {
                    <s25-ng-ingestion-form
                        [ingestionPointId]="activeFormId()!"
                        [eventId]="eventStore.eventId() ?? undefined"
                        [runtimeHydrate]="activeEventEditRuntimeHydrate()"
                        [showFormExitActions]="true"
                        (onSubmit)="resetForm()"
                        (onClose)="resetForm()"
                    />
                } @else if (eventEditForms().length === 0) {
                    <p>No edit forms available for this event.</p>
                } @else {
                    <div class="forms-grid">
                        @for (form of eventEditForms(); track form.ingestionPointId) {
                            <s25-ng-ingestion-form-tile
                                [form]="form"
                                [actionLabel]="'Edit Event'"
                                (formStart)="activeFormId.set($event)"
                            />
                        }
                    </div>
                }
            </section>
        }
    `,
    styles: `
        .event-edit-forms {
            margin-top: 2rem;
        }

        .forms-grid {
            display: grid;
            grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
            gap: 1rem;
        }
    `,
    encapsulation: ViewEncapsulation.Emulated,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [IngestionFormTileComponent, IngestionFormComponent],
})
export class SeEventEditFormsWidgetComponent extends LayoutWidget<typeof SeEventEditFormsWidgetComponent> {
    static readonly META = {
        name: "Edit Event Forms",
        info: "Connect25 forms that let users edit this event",
        settings: [],
        defaultSettings: {},
    } as const satisfies Layouts.WidgetStatic;

    protected readonly eventStore = inject(SEEventDetailsStore);
    private readonly orgSecStore = inject(SeOrgSecurityStore);
    private readonly loginStore = inject(LoginStore);

    readonly activeFormId = signal<number | undefined>(undefined);

    readonly canShowForms = computed(() => this.loginStore.loggedIn() && this.orgSecStore.canCreateEvent());

    readonly activeEventEditRuntimeHydrate = computed(() =>
        this.activeFormId() != null ? this.eventEditRuntimeHydrate() : undefined,
    );

    private readonly eventEditRuntimeHydrate = linkedSignal({
        source: () => this.eventStore.eventId(),
        computation: (eventId: number | undefined) => (eventId != null ? createEventEditHydrate(eventId) : undefined),
    });

    private readonly hostOrgId = computed(() => this.eventStore.hostOrg()?.itemId);

    private readonly orgScopedFormsGet = s25Resource({
        if: () => this.loginStore.loggedIn() && this.hostOrgId() != null,
        params: () => this.hostOrgId(),
        loader: async ({ params: organizationId }) => {
            return DataIngestionService.getIngestionPoints(
                IngestionPointCategory.ENGAGEMENT,
                String(organizationId),
                false,
                ["eventEdit"],
                IngestionAccess.IngestionPointAction.FILL,
            ).catch((error) => {
                console.warn(S25Util.errorText(error));
                return [] as IngestionPoint[];
            });
        },
    });

    private readonly globalEventEditFormsGet = s25Resource({
        if: () => this.loginStore.loggedIn() && !this.hasOrgScopedEventEdit(),
        loader: async () => {
            return DataIngestionService.getIngestionPoints(
                IngestionPointCategory.ENGAGEMENT,
                undefined,
                false,
                ["eventEdit"],
                IngestionAccess.IngestionPointAction.FILL,
            ).catch((error) => {
                console.warn(S25Util.errorText(error));
                return [] as IngestionPoint[];
            });
        },
    });

    private readonly orgScopedFiltered = computed(() =>
        this.filterEnabledActiveEventEditForms(this.orgScopedFormsGet.value()),
    );

    private readonly hasOrgScopedEventEdit = computed(() =>
        this.orgScopedFiltered().some((point) => Boolean(point.use?.eventEdit)),
    );

    private readonly globalEventEditFiltered = computed(() =>
        this.filterEnabledActiveEventEditForms(this.globalEventEditFormsGet.value()),
    );

    readonly eventEditForms = computed(() => {
        const orgScoped = this.orgScopedFiltered();
        if (this.hasOrgScopedEventEdit()) {
            return orgScoped;
        }
        return this.globalEventEditFiltered();
    });

    resetForm = () => {
        this.activeFormId.set(undefined);
    };

    private filterEnabledActiveEventEditForms(forms: IngestionPoint[] | undefined): IngestionPoint[] {
        return (forms ?? []).filter((form) => {
            const isEnabled = form.enabled && !form.deleted;
            const isActive = form.active !== false;
            return isEnabled && isActive && Boolean(form.use?.eventEdit);
        });
    }
}
